import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { applyGtagConsent, persistConsent, readStoredConsent, type LyrraConsentChoice } from '@/lib/google-consent';

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => readStoredConsent() === null);
  const titleId = useId();

  const choose = useCallback((choice: LyrraConsentChoice) => {
    persistConsent(choice);
    applyGtagConsent(choice);
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  const title = String(t('consent.bannerTitle'));
  const description = String(t('consent.bannerDescription'));
  const acceptAll = String(t('consent.acceptAll'));
  const essentialOnly = String(t('consent.essentialOnly'));
  const manage = String(t('consent.manageCookies'));
  const region = String(t('consent.regionNotice'));

  return (
    <div className="fixed inset-0 z-[100] flex flex-col justify-end" role="presentation">
      {/* Voile : pas de fermeture sans choix explicite (cookies non essentiels bloqués côté tags tant que refus / pas de clic). */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-12px_40px_rgba(0, 27, 61,0.18)]"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6">
          <div className="min-w-0 flex-1">
            <p id={titleId} className="text-sm font-bold text-slate-900">
              {title}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-slate-700">{description}</p>
            <p className="mt-2 text-xs text-slate-700">{region}</p>
          </div>
          <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl bg-[color:var(--inst-player-primary-deep,#000F24)] px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95"
              onClick={() => choose('all')}
              autoFocus
            >
              {acceptAll}
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
              onClick={() => choose('essential')}
            >
              {essentialOnly}
            </button>
            <Link
              to="/cookies"
              className="inline-flex items-center justify-center rounded-xl px-3 py-2.5 text-sm font-semibold text-[color:var(--inst-player-primary-deep,#000F24)] underline-offset-2 hover:underline"
            >
              {manage}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
